import axios from 'axios'
import { saveAuthSession, clearAuthSession, getAuthSessionGeneration } from './authSession';
import router from '../router'; 
import { Message } from 'element-ui'
import tenantEndpoint from './tenantEndpoint';
import { attachSecurityMeta } from './securityMeta';
import { protectRequestBody } from './requestBodyCrypto';

const http = axios.create({
	timeout: 1000 * 30
})
let refreshTokenPromise = null;

function showMessage(type, message) {
	if (!message) {
		return;
	}
	Message({
		message,
		type,
		duration: 1500,
		customClass: `element-${type}-message-zindex`
	})
}

function shouldSkipErrorMessage(config) {
	return Boolean(config && config._skipErrorMessage);
}

function getRequestUrl(configOrUrl) {
	if (typeof configOrUrl === 'string') {
		return configOrUrl;
	}
	return String((configOrUrl && configOrUrl.url) || '');
}

function isRefreshRequest(configOrUrl) {
	return getRequestUrl(configOrUrl).includes('/refreshToken');
}

function normalizeStatus(error) {
	const status = error && (error.status || (error.response && error.response.status) || error.code);
	const parsed = Number(status);
	return Number.isFinite(parsed) ? parsed : 0;
}

function getErrorMessage(error) {
	if (!error) {
		return '';
	}
	if (typeof error === 'string') {
		return error;
	}
	return String(error.message || (error.response && error.response.data) || '').trim();
}

function createAuthError(code, message) {
	const error = new Error(message || '登录状态已失效');
	error.code = code;
	return error;
}

function isFatalRefreshFailure(error) {
	const code = error && error.code !== undefined ? String(error.code) : '';
	const status = normalizeStatus(error);
	const message = getErrorMessage(error);
	if (status === 400 || status === 401) {
		return true;
	}
	if ([
		'REAUTH_REQUIRED',
		'REFRESH_TOKEN_MISSING',
		'TOKEN_REFRESH_INVALID_RESPONSE'
	].includes(code)) {
		return true;
	}
	return /登录信息已过期|重新登录|未登录|token无效|token已过期|用户不存在|已注销|被封禁/.test(message);
}

function emitExit() {
	clearAuthSession();
	if (router && router.app && router.app.$eventBus) {
		router.app.$eventBus.$emit("exit")
	}
}

function isAuthRoutePath(path) {
	return ['/tenant', '/login', '/register', '/password/reset'].includes(String(path || ''));
}

function shouldSuppressTenantEndpointMissingPrompt() {
	const currentPath = router && router.currentRoute ? router.currentRoute.path : '';
	if (isAuthRoutePath(currentPath)) {
		return true;
	}
	const accessToken = sessionStorage.getItem("accessToken");
	const refreshToken = sessionStorage.getItem("refreshToken");
	return !accessToken && !refreshToken;
}

function redirectToTenantEntry() {
	const currentRoute = router && router.currentRoute ? router.currentRoute : null;
	if (currentRoute && currentRoute.path === '/tenant') {
		return;
	}
	const redirect = currentRoute && typeof currentRoute.fullPath === 'string'
		&& currentRoute.fullPath.startsWith('/')
		&& !currentRoute.fullPath.startsWith('/tenant')
		? currentRoute.fullPath
		: '';
	router.replace({
		path: '/tenant',
		query: redirect ? { redirect } : {}
	}).catch(() => {})
}

// 刷新 token 需要单飞，避免空闲唤醒后并发 401 抢刷导致会话被错误退出。
export function refreshAccessToken() {
	if (refreshTokenPromise) {
		return refreshTokenPromise;
	}
	const refreshToken = sessionStorage.getItem("refreshToken");
	if (!refreshToken) {
		return Promise.reject(createAuthError('REFRESH_TOKEN_MISSING', '登录状态已失效，请重新登录'));
	}
	const generation = getAuthSessionGeneration();
	const tenantKey = tenantEndpoint.getCurrentSession()?.tenantKey;
	refreshTokenPromise = http({
		method: 'put',
		url: '/refreshToken',
		headers: {
			refreshToken
		},
		_skipAuthRefresh: true
	}).then((data) => {
		if (generation !== getAuthSessionGeneration() || tenantKey !== tenantEndpoint.getCurrentSession()?.tenantKey) {
			throw createAuthError('AUTH_CONTEXT_CHANGED', '登录上下文已改变');
		}
		if (!data || !data.accessToken || !data.refreshToken) {
			throw createAuthError('TOKEN_REFRESH_INVALID_RESPONSE', '登录状态已失效，请重新登录');
		}
		saveAuthSession(data);
		return data;
	}).catch(error => {
		if (generation === getAuthSessionGeneration() && isFatalRefreshFailure(error)) clearAuthSession();
		throw error;
	}).finally(() => {
		refreshTokenPromise = null;
	});
	return refreshTokenPromise;
}

function retryOriginalRequest(originalConfig) {
	const nextConfig = {
		...originalConfig,
		_retriedAfterRefresh: true,
		headers: {
			...(originalConfig.headers || {})
		}
	};
	delete nextConfig.headers.accessToken;
	return http(nextConfig);
}

async function handleUnauthorized(originalConfig, fallbackError) {
	if (originalConfig._authGeneration !== undefined && originalConfig._authGeneration !== getAuthSessionGeneration()) {
		return Promise.reject(createAuthError('AUTH_CONTEXT_CHANGED', '登录上下文已改变'));
	}
	const requestUrl = getRequestUrl(originalConfig);
	if (isRefreshRequest(requestUrl) || originalConfig._retriedAfterRefresh || originalConfig._skipAuthRefresh) {
		const authError = createAuthError('REAUTH_REQUIRED', '登录状态已失效，请重新登录');
		emitExit();
		return Promise.reject(fallbackError || authError);
	}
	try {
		await refreshAccessToken();
		return retryOriginalRequest(originalConfig);
	} catch (error) {
		if (isFatalRefreshFailure(error)) {
			emitExit();
		}
		return Promise.reject(error);
	}
}

/**
 * 请求拦截
 */
http.interceptors.request.use(async config => {
	config._authGeneration = getAuthSessionGeneration();
	const url = String(config.url || '');
	const isAbsolute = /^https?:\/\//i.test(url);
	if (!isAbsolute) {
		const session = tenantEndpoint.getCurrentSession();
		if (!session || !session.apiBaseUrl) {
			return Promise.reject(new Error('TENANT_ENDPOINT_MISSING'));
		}
		config.baseURL = session.apiBaseUrl;
	}
	let accessToken = sessionStorage.getItem("accessToken");
	if (accessToken) {
		config.headers.accessToken = encodeURIComponent(accessToken);
	}
	const securedConfig = await attachSecurityMeta(config);
	return protectRequestBody(securedConfig)
}, error => {
	return Promise.reject(error)
})

/**
 * 响应拦截
 */
http.interceptors.response.use(async response => {
	if (response.data.code == 200) {
		return response.data.data;
	} else if (response.data.code == 400) {
		const authError = createAuthError('NO_LOGIN', response.data.message || '登录状态已失效，请重新登录');
		return handleUnauthorized(response.config || {}, authError);
	} else if (response.data.code == 401) {
		return handleUnauthorized(response.config || {}, response.data);
	} else {
		if (!shouldSkipErrorMessage(response.config)) {
			showMessage('error', response.data.message);
		}
		return Promise.reject(response.data)
	}
}, error => {
	if (error && error.message === 'TENANT_ENDPOINT_MISSING') {
		if (shouldSuppressTenantEndpointMissingPrompt()) {
			return Promise.reject(error)
		}
		redirectToTenantEntry()
		return Promise.reject(error)
	}
	if (!error.response) {
		const reqUrl = (error.config && error.config.url) ? String(error.config.url) : '';
		const isUploadRequest = reqUrl.includes('/upload');
		if (!shouldSkipErrorMessage(error.config)) {
			showMessage('error', isUploadRequest ? '上传失败，请稍后重试' : '网络似乎有点不给力哟')
		}
		return Promise.reject(error)
	}
	switch (error.response.status) {
		case 400:
			if (!shouldSkipErrorMessage(error.config)) {
				showMessage('error', error.response.data)
			}
			break
		case 401:
			return handleUnauthorized(error.config || {}, error)
		case 405:
			if (!shouldSkipErrorMessage(error.config)) {
				showMessage('error', 'http请求方式有误')
			}
			break
		case 413:
			if (!shouldSkipErrorMessage(error.config)) {
				showMessage('error', '上传失败，文件大小超出限制(413)')
			}
			break
		case 404:
		case 500:
			if (!shouldSkipErrorMessage(error.config)) {
				showMessage('error', '服务器出了点小差，请稍后再试')
			}
			break
		case 501:
			if (!shouldSkipErrorMessage(error.config)) {
				showMessage('error', '服务器不支持当前请求所需要的某个功能')
			}
			break
	}

	return Promise.reject(error)
})

export default http
